import React, { useCallback, useEffect, useState } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import { adminApi } from '../../services/api';
import { useToast } from '../hooks/useToast';

const emptyZone = { name: '', state: '', city: '', pincodes: '', charge: 0, estimatedDays: 7, isActive: true };

const Delivery = () => {
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState(emptyZone);
  const [editingId, setEditingId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { success, warning, error: showError } = useToast();

  const loadZones = useCallback(async () => {
    try {
      const response = await adminApi.listDeliveryZones();
      setZones(response.data?.data || []);
      setSelectedIds([]);
    } catch (error) {
      warning(error.response?.data?.message || 'Failed to load delivery zones');
    }
  }, [warning]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const saveZone = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      charge: Number(form.charge),
      estimatedDays: Number(form.estimatedDays),
      pincodes: form.pincodes.split(',').map((pincode) => pincode.trim()).filter(Boolean)
    };
    try {
      if (editingId) {
        await adminApi.updateDeliveryZone(editingId, payload);
        success('Delivery zone updated');
      } else {
        await adminApi.saveDeliveryZone(payload);
        success('Delivery zone created');
      }
      setForm(emptyZone);
      setEditingId('');
      loadZones();
    } catch (error) {
      warning(error.response?.data?.message || 'Could not save delivery zone');
    }
  };

  const editZone = (zone) => {
    setEditingId(zone._id);
    setForm({
      name: zone.name || '',
      state: zone.state || '',
      city: zone.city || '',
      pincodes: (zone.pincodes || []).join(', '),
      charge: zone.charge ?? 0,
      estimatedDays: zone.estimatedDays ?? 7,
      isActive: zone.isActive !== false
    });
  };

  const resetForm = () => {
    setForm(emptyZone);
    setEditingId('');
  };

  const toggleSelection = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const allSelected = zones.length > 0 && zones.every((zone) => selectedIds.includes(zone._id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : zones.map((zone) => zone._id));

  const deleteZones = async (ids) => {
    if (!ids.length) return;
    try {
      setDeleting(true);
      if (ids.length === 1) {
        await adminApi.deleteDeliveryZone(ids[0]);
      } else {
        await adminApi.bulkDeleteDeliveryZones(ids);
      }
      success(ids.length === 1 ? 'Delivery zone deleted' : `${ids.length} delivery zones deleted`);
      setDeleteTarget(null);
      setBulkDeleteOpen(false);
      loadZones();
    } catch (error) {
      showError(error.response?.data?.message || 'Could not delete delivery zone');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery Management"
        description="Manage serviceable pincodes and delivery charges"
        breadcrumbs={['Home', 'Delivery']}
      />

      <form onSubmit={saveZone} className="bg-white rounded-xl border border-gray-100 p-6 grid md:grid-cols-3 gap-4">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Zone name" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required placeholder="State" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required placeholder="City" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.pincodes} onChange={(e) => setForm({ ...form, pincodes: e.target.value })} required placeholder="Pincodes, comma separated" className="md:col-span-3 border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.charge} onChange={(e) => setForm({ ...form, charge: e.target.value })} type="number" min="0" required placeholder="Delivery charge" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.estimatedDays} onChange={(e) => setForm({ ...form, estimatedDays: e.target.value })} type="number" min="1" required placeholder="Estimated days" className="border border-gray-200 rounded-lg px-4 py-2" />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
          Active zone
        </label>
        <div className="md:col-span-2 flex justify-end gap-3">
          {editingId && (
            <button type="button" onClick={resetForm} className="rounded-lg border border-gray-200 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-50">
              Cancel Edit
            </button>
          )}
          <button className="bg-amber-600 text-white rounded-lg px-4 py-2 font-semibold hover:bg-amber-700">
            {editingId ? 'Update Zone' : 'Create Zone'}
          </button>
        </div>
      </form>

      <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          {selectedIds.length > 0 ? `${selectedIds.length} delivery zone${selectedIds.length === 1 ? '' : 's'} selected` : 'Select delivery zones for bulk delete'}
        </p>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          onClick={() => setBulkDeleteOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 size={16} />
          Delete Selected
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all delivery zones" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Zone</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Location</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Pincodes</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Charge</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">ETA</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {zones.map((zone) => (
              <tr key={zone._id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900">
                  <input type="checkbox" checked={selectedIds.includes(zone._id)} onChange={() => toggleSelection(zone._id)} aria-label={`Select ${zone.name}`} />
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{zone.name}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{zone.city}, {zone.state}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{zone.pincodes?.join(', ')}</td>
                <td className="px-6 py-4 text-sm text-gray-900">Rs. {Number(zone.charge || 0).toLocaleString('en-IN')}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{zone.estimatedDays} days</td>
                <td className="px-6 py-4 text-sm text-gray-900">{zone.isActive === false ? 'Inactive' : 'Active'}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="flex gap-2">
                    <button type="button" onClick={() => editZone(zone)} className="rounded-lg p-2 text-amber-700 hover:bg-amber-100" aria-label={`Edit ${zone.name}`}>
                      <Edit2 size={16} />
                    </button>
                    <button type="button" onClick={() => setDeleteTarget(zone)} className="rounded-lg p-2 text-red-700 hover:bg-red-100" aria-label={`Delete ${zone.name}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {zones.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">No delivery zones found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteZones([deleteTarget?._id].filter(Boolean))}
        title="Delete Delivery Zone"
        message={`Delete "${deleteTarget?.name || 'this delivery zone'}"?`}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        isDangerous
      />

      <ConfirmDialog
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => deleteZones(selectedIds)}
        title="Bulk Delete Delivery Zones"
        message={`Delete ${selectedIds.length} selected delivery zone${selectedIds.length === 1 ? '' : 's'}?`}
        confirmText={deleting ? 'Deleting...' : 'Delete Selected'}
        isDangerous
      />
    </div>
  );
};

export default Delivery;
